<!--
  OPDS Search Bar Component
  Allows searching in OPDS catalogs with support for title and author filters
-->
<script lang="ts">
  import { opdsStore } from '../stores/opds.svelte';
  import type { OPDSSearchParams } from '@legere/opds-client';

  let searchQuery = $state('');
  let searchMode: 'all' | 'title' | 'author' = $state('all');
  let isSearching = $state(false);

  async function handleSearch(e: Event) {
    e.preventDefault();

    if (!searchQuery.trim()) {
      return;
    }

    isSearching = true;

    try {
      const params: OPDSSearchParams = {};

      if (searchMode === 'all') {
        params.searchTerms = searchQuery;
      } else if (searchMode === 'title') {
        params.title = searchQuery;
      } else if (searchMode === 'author') {
        params.author = searchQuery;
      }

      await opdsStore.search(params);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      isSearching = false;
    }
  }

  function handleClear() {
    searchQuery = '';
    opdsStore.clearSearch();
  }
</script>

<div class="search-bar bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
  <form on:submit={handleSearch} class="flex gap-2">
    <div class="flex-1 flex gap-2">
      <input
        type="text"
        bind:value={searchQuery}
        placeholder={searchMode === 'all' ? 'Search books...' : searchMode === 'title' ? 'Search by title...' : 'Search by author...'}
        class="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600
               bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
        disabled={isSearching}
      />

      <select
        bind:value={searchMode}
        class="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
               bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100
               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
        disabled={isSearching}
      >
        <option value="all">All</option>
        <option value="title">Title</option>
        <option value="author">Author</option>
      </select>
    </div>

    <button
      type="submit"
      disabled={isSearching || !searchQuery.trim()}
      class="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600
             text-white font-medium transition-colors
             disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
    >
      {isSearching ? 'Searching...' : 'Search'}
    </button>

    {#if opdsStore.isSearchMode}
      <button
        type="button"
        on:click={handleClear}
        class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600
               bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300
               hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        Clear
      </button>
    {/if}
  </form>

  {#if opdsStore.isSearchMode && opdsStore.searchQuery}
    <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
      Showing results for: <span class="font-medium text-gray-900 dark:text-gray-100">{opdsStore.searchQuery}</span>
    </div>
  {/if}
</div>

<style>
  .search-bar {
    position: sticky;
    top: 0;
    z-index: 10;
  }
</style>
